import { create } from 'zustand';
import { WorkingState, ChatMessage, FabricationStage, DiagnosticProbe, ProbeRunRecord, CapabilityEntry } from '../types/persona_engine';
import { assembleControllerPrompt, computeStateHashes, computeSourceHash, migrateCapabilities } from '../utils/persona_engine_utils';
import * as service from '../services/personaEngineeringService';
import { v4 as uuidv4 } from 'uuid';
import api from '../services/api';

// ── Safe merge helper ────────────────────────────────────────────────
// Backend actions (mutateSource, generateControls, compile) return a full
// WorkingState, but they only OWN certain fields.  Doing `set({ state: response })`
// clobbers everything else — probes, capabilities, active_panel_index, etc.
// This helper merges only the fields the backend action actually touched.
//
// `owned` = keys the backend action is allowed to overwrite.
// Everything else is preserved from the current in-memory state.
function mergeBackendResponse(
  current: WorkingState,
  response: WorkingState,
  owned: (keyof WorkingState)[],
): WorkingState {
  const merged = { ...current };
  for (const key of owned) {
    (merged as any)[key] = (response as any)[key];
  }
  return merged;
}

// Fields owned by each backend action
const SOURCE_OWNED: (keyof WorkingState)[] = [
  'character_bible', 'tethics', 'patterns', 'antipatterns',
  'conversation_source', 'suggested_names', 'tags',
  'controller_prompt', 'version', 'last_updated',
];
const CONTROLS_OWNED: (keyof WorkingState)[] = [
  'ui_schema', 'conversation_cockpit', 'controller_prompt',
  'version', 'last_updated',
];
const COMPILE_OWNED: (keyof WorkingState)[] = [
  'system_prompt', 'conversation_verification', 'system_prompt_hash',
  'controller_prompt', 'last_compiled_source_hash',
  'version', 'last_updated',
];

// UUID generation utility function (fallback for environments without crypto.randomUUID)
function generateUUID(): string {
  // Use the uuid library if available
  if (typeof uuidv4 === 'function') {
    return uuidv4();
  }
  
  // Simple UUID v4 implementation as fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

interface FabricationStore {
  state: WorkingState | null;
  isLoading: boolean;
  error: string | null;
  currentPersonaId: string | null;
  isDirty: boolean;

  // Hash tracking (used by backend for cache/change detection)
  current_source_hash: string | null;
  artifact_source_hash: string | null;

  // Auto-compile UI state (not persisted in WorkingState)
  auto_mode: boolean;
  needs_recompile: boolean;

  // Autosave
  autoSaveEnabled: boolean;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastAutoSavedAt: string | null;
  setAutoSaveEnabled: (enabled: boolean) => void;
  triggerAutoSave: () => Promise<void>;
  stage_completion: {
    genesis:      boolean;
    tuning:       boolean;
    artifact:     boolean;
    probes:       boolean;
    capabilities: boolean;
  };

  // Cross-vertical event bridge: fires a message into TestDrivePanel
  pendingTestDriveMessage: string | null;
  setPendingTestDriveMessage: (msg: string | null) => void;

  // Cross-vertical event bridge: flies the map to a specific node
  pendingMapFocus: string | null;
  setPendingMapFocus: (id: string | null) => void;

  // Whether controls have been auto-generated on first Tuning tab visit
  hasAutoGeneratedControls: boolean;

  // Capability extraction loading/error (separate from main isLoading)
  capabilitiesLoading: boolean;
  capabilitiesError: string | null;

  // Probe-specific loading/error (separate from main isLoading)
  probesLoading: boolean;
  probesError: string | null;

  // Per-probe session state (not persisted in WorkingState)
  probeRunStates: Record<string, 'idle' | 'running' | 'awaiting_judgment'>;

  // Active probe being run (drives TestDrivePanel probe mode indicator)
  activeProbeId: string | null;

  // Pending judgment — set after TestDrivePanel returns a response for a probe
  pendingJudgmentProbeId: string | null;
  pendingJudgmentResponse: string | null;

  // Capability actions
  extractCapabilities: (count?: number, depth?: 'brief' | 'standard' | 'deep') => Promise<void>;
  updateCapability: (key: string, value: number) => void;
  addCapability: (key: string, value: number) => void;
  removeCapability: (key: string) => void;

  // Core actions
  init: (intent: string) => Promise<void>;
  sendSourceMessage: (message: string) => Promise<void>;
  generateControls: (instruction?: string) => Promise<void>;
  updateControl: (id: string, value: any) => void;
  runCompile: () => Promise<void>;
  sendTestDriveMessage: (message: string) => Promise<string>;

  // New method for direct source updates
  updateSourceData: (updates: Partial<{
    character_bible: string;
    tethics: string[];
    patterns: string[];
    antipatterns: string[];
  }>) => Promise<void>;

  // UI state actions
  setAutoMode: (enabled: boolean) => void;
  setActivePanel: (index: number) => void;

  // Advanced actions
  bake: () => Promise<void>;
  publish: (personaId?: string) => Promise<string>;

  // Probe actions
  generateProbes: (count: number, focusAreas?: string[]) => Promise<void>;
  markProbeResult: (probeId: string, passed: boolean, response: string, notes?: string) => Promise<void>;
  deleteProbe: (probeId: string) => void;
  addCustomProbe: (probe: Omit<DiagnosticProbe, 'id' | 'run_history' | 'created_at' | 'is_custom'>) => void;
  updateProbe: (probeId: string, updates: Partial<DiagnosticProbe>) => void;
  setActiveProbeId: (id: string | null) => void;
  clearPendingJudgment: () => void;

  // Save/load actions
  savePersona: (name: string, description?: string, isPublic?: boolean, forceNew?: boolean) => Promise<string>;
  // (triggerAutoSave is declared above with autosave fields)
  loadPersona: (personaId: string) => Promise<boolean>;
  remixPersona: (personaId: string, name?: string, description?: string) => Promise<boolean>;
  receiveSendTo: (payload: { content: string; action: 'genesis' | 'mutate' | 'evidence' }) => void;
  markClean: () => void;
  resetState: () => void;
}

// Helper to compute stage_completion from a WorkingState
function computeStageCompletion(state: WorkingState | null) {
  return {
    genesis:      !!state?.character_bible?.length,
    tuning:       (state?.ui_schema?.length ?? 0) > 0,
    artifact:     !!state?.system_prompt?.length,
    probes:       (state?.probes?.length ?? 0) > 0 &&
                  (state?.probes?.some(p => p.last_passed !== undefined && p.last_passed !== null) ?? false),
    capabilities: Object.keys(state?.capabilities || {}).length > 0,
  };
}

export const useFabricationStore = create<FabricationStore>((set, get) => {
  return {
    state: null,
    isLoading: false,
    error: null,
    currentPersonaId: null,
    isDirty: false,
    current_source_hash: null,
    artifact_source_hash: null,
    auto_mode: true,
    needs_recompile: false,
    stage_completion: { genesis: false, tuning: false, artifact: false, probes: false, capabilities: false },
    autoSaveEnabled: localStorage.getItem('forgeAutoSaveEnabled') !== 'false', // default ON
    autoSaveStatus: 'idle' as const,
    lastAutoSavedAt: null,
    pendingTestDriveMessage: null,
    setPendingTestDriveMessage: (msg) => set({ pendingTestDriveMessage: msg }),

    pendingMapFocus: null,
    setPendingMapFocus: (id) => set({ pendingMapFocus: id }),

    setAutoSaveEnabled: (enabled: boolean) => {
      localStorage.setItem('forgeAutoSaveEnabled', String(enabled));
      set({ autoSaveEnabled: enabled, autoSaveStatus: 'idle' });
    },

    triggerAutoSave: async () => {
      const { state, currentPersonaId, isLoading, autoSaveStatus } = get();
      // Guards: need a saved persona, a name, not already saving, not mid-compile
      if (!state || !currentPersonaId || isLoading || autoSaveStatus === 'saving') return;
      const name = state.display_name?.trim();
      if (!name) return;

      set({ autoSaveStatus: 'saving' });
      try {
        await get().savePersona(name, state.description ?? '', false, false);
        set({
          autoSaveStatus: 'saved',
          lastAutoSavedAt: new Date().toISOString(),
        });
        // Reset to idle after 4s so the UI doesn't stay green forever
        setTimeout(() => {
          // Only reset if still 'saved' (not a subsequent error)
          if (get().autoSaveStatus === 'saved') {
            set({ autoSaveStatus: 'idle' });
          }
        }, 4000);
      } catch (e) {
        set({ autoSaveStatus: 'error' });
      }
    },
    hasAutoGeneratedControls: false,
    capabilitiesLoading: false,
    capabilitiesError: null,
    probesLoading: false,
    probesError: null,
    probeRunStates: {},
    activeProbeId: null,
    pendingJudgmentProbeId: null,
    pendingJudgmentResponse: null,
    
    init: async (intent: string) => {
      set({ isLoading: true, error: null });
      try {
        const response = await service.genesis(intent);
        
        // Ensure controller_prompt is set
        let state = response;
        if (!state.controller_prompt) {
          state.controller_prompt = assembleControllerPrompt(state);
        }
        
        // Compute hashes
        state = await computeStateHashes(state);
        
        // Compute source hash
        const sourceHash = await computeSourceHash(state);
        
        set({
          state,
          isLoading: false,
          current_source_hash: sourceHash,
          artifact_source_hash: sourceHash,
          auto_mode: true,
          needs_recompile: false,
          // Freshly generated persona has no DB record yet — treat as dirty
          // so the nav-blocker catches leave-without-save. Mirrors remixPersona.
          isDirty: true,
          stage_completion: computeStageCompletion(state),
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Unknown error',
          isLoading: false
        });
      }
    },

    // New method for direct source updates
    updateSourceData: async (updates) => {
      const { state } = get();
      if (!state) return;
      
      // Soft warning if stage is locked (don't block)
      if (state.stage_locked && state.stage !== FabricationStage.GENESIS) {
        set({ error: "Warning: Source stage is locked. Changes may not persist." });
      }
      
      // Apply updates to state
      const updatedState = {
        ...state,
        ...updates
      };
      
      // Compute new source hash
      const newSourceHash = await computeSourceHash(updatedState);
      
      // Set the updated state with new hash
      set({
        state: updatedState,
        current_source_hash: newSourceHash,
        isDirty: true,
        needs_recompile: true,
        stage_completion: computeStageCompletion(updatedState),
      });
    },
    
    sendSourceMessage: async (message: string) => {
      const { state } = get();
      if (!state) return;
      
      // Soft warning if stage is locked (don't block)
      if (state.stage_locked && state.stage !== FabricationStage.GENESIS) {
        set({ error: "Warning: Source stage is locked. Changes may not persist." });
      }
      
      set({ isLoading: true, error: null });
      
      // Optimistically update UI
      const newMessage: ChatMessage = {
        id: generateUUID(),
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      };
      
      const updatedState = {
        ...state,
        conversation_source: [...state.conversation_source, newMessage]
      };
      
      // Ensure controller_prompt is up to date
      updatedState.controller_prompt = assembleControllerPrompt(updatedState);
      const stateWithHashes = await computeStateHashes(updatedState);
      
      set({ state: stateWithHashes });
      
      try {
        const response = await service.mutateSource(stateWithHashes, message);

        // Merge only source-owned fields — preserve probes, capabilities, active_panel_index
        let responseWithPrompt = { ...response };
        responseWithPrompt.controller_prompt = assembleControllerPrompt(responseWithPrompt);
        responseWithPrompt = await computeStateHashes(responseWithPrompt);

        const current = get().state!;
        const merged = mergeBackendResponse(current, responseWithPrompt, SOURCE_OWNED);
        // Also merge conversation + hashes that computeStateHashes produced
        merged.system_prompt_hash = responseWithPrompt.system_prompt_hash ?? current.system_prompt_hash;

        set({
          state: merged,
          isLoading: false,
          isDirty: true,
          needs_recompile: true,
          hasAutoGeneratedControls: true,
          stage_completion: computeStageCompletion(merged),
        });
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Unknown error', 
          isLoading: false 
        });
      }
    },
    
    generateControls: async (instruction?: string) => {
      const { state, isLoading } = get();
      if (!state || isLoading) return;

      // Soft warning if stage is locked (don't block)
      if (state.stage_locked && state.stage !== FabricationStage.TUNING) {
        set({ error: "Warning: Controls stage is locked. Changes may not persist." });
      }

      set({ isLoading: true, error: null, hasAutoGeneratedControls: true });
      
      // Ensure controller_prompt is up to date
      let updatedState = {
        ...state,
        controller_prompt: assembleControllerPrompt(state)
      };
      updatedState = await computeStateHashes(updatedState);
      
      try {
        const response = await service.generateControls(updatedState, instruction);

        let responseHashed = await computeStateHashes(response);

        // Merge only controls-owned fields — preserve probes, capabilities, active_panel_index
        const current = get().state!;
        const merged = mergeBackendResponse(current, responseHashed, CONTROLS_OWNED);

        set({
          state: merged,
          isLoading: false,
          isDirty: true,
          needs_recompile: true,
          stage_completion: computeStageCompletion(merged),
        });
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Unknown error', 
          isLoading: false 
        });
      }
    },
    
    updateControl: (id: string, value: any) => {
      const { state } = get();
      if (!state) return;
      
      // Soft warning if stage is locked (don't block)
      if (state.stage_locked && state.stage !== FabricationStage.TUNING) {
        set({ error: "Warning: Controls stage is locked. Changes may not persist." });
      }
      
      const updatedSchema = state.ui_schema.map(control => 
        control.id === id ? { ...control, current_value: value } : control
      );
      
      const updatedState = { ...state, ui_schema: updatedSchema };
      
      set({
        state: updatedState,
        isDirty: true,
        needs_recompile: true,
        stage_completion: computeStageCompletion(updatedState),
      });
    },
    
    runCompile: async () => {
      const { state, current_source_hash, isLoading } = get();
      if (!state || isLoading) return;

      set({ isLoading: true, error: null });
      
      // Ensure controller_prompt is up to date
      let updatedState = {
        ...state,
        controller_prompt: assembleControllerPrompt(state)
      };
      updatedState = await computeStateHashes(updatedState);
      
      try {
        const response = await service.compile(updatedState);

        let responseHashed = await computeStateHashes(response);

        // Merge only compile-owned fields — preserve probes, capabilities, active_panel_index
        const current = get().state!;
        const merged = mergeBackendResponse(current, responseHashed, COMPILE_OWNED);

        set({
          state: merged,
          isLoading: false,
          isDirty: true,
          artifact_source_hash: current_source_hash,
          needs_recompile: false,
          stage_completion: computeStageCompletion(merged),
        });

        // Non-blocking — capabilities extract in background after compile
        get().extractCapabilities().catch(e =>
          console.warn('Capability extraction failed (non-fatal):', e)
        );
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Unknown error', 
          isLoading: false 
        });
      }
    },
    
    sendTestDriveMessage: async (message: string) => {
      const { state } = get();
      if (!state) return '';

      try {
        const response = await service.testdriveChat(state, message);
        // Normalize: backend may return string or { reply: string }
        return typeof response === 'string'
          ? response
          : (response && typeof response === 'object' && 'reply' in response)
            ? response.reply
            : String(response);
      } catch (error) {
        console.error('sendTestDriveMessage failed:', error);
        set({ error: error instanceof Error ? error.message : 'Unknown error' });
        return 'Error: Unable to get response';
      }
    },
    
    setAutoMode: (enabled: boolean) => {
      set({ auto_mode: enabled });
    },
    
    setActivePanel: (index: number) => {
      const { state } = get();
      if (!state) return;
      
      set({
        state: {
          ...state,
          active_panel_index: index
        }
      });
    },
    
    bake: async () => {
      const { state } = get();
      if (!state) return;

      set({ isLoading: true, error: null });

      try {
        const response = await service.bake(state);

        let responseWithPrompt = { ...response };
        responseWithPrompt.controller_prompt = assembleControllerPrompt(responseWithPrompt);
        responseWithPrompt = await computeStateHashes(responseWithPrompt);

        // Bake touches source fields — merge, don't replace
        const current = get().state!;
        const merged = mergeBackendResponse(current, responseWithPrompt, SOURCE_OWNED);

        set({
          state: merged,
          isLoading: false,
          needs_recompile: true,
          stage_completion: computeStageCompletion(merged),
        });
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Unknown error', 
          isLoading: false 
        });
      }
    },
    
    publish: async (personaId?: string) => {
      const { state } = get();
      if (!state) return '';
      
      set({ isLoading: true, error: null });
      
      try {
        const result = await service.publish(state, personaId);
        set({ isLoading: false });
        return result;
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Unknown error', 
          isLoading: false 
        });
        return '';
      }
    },
    
    savePersona: async (
      name: string, 
      description?: string, 
      isPublic?: boolean,
      forceNew?: boolean
    ) => {
      const { state, currentPersonaId } = get();
      if (!state) return '';
      
      set({ isLoading: true, error: null });
      
      try {
        // Save guard — auto-extract capabilities if prompt exists but caps are empty
        const currentState = get().state;
        if (
          currentState?.system_prompt &&
          (!currentState.capabilities || Object.keys(currentState.capabilities).length === 0)
        ) {
          await get().extractCapabilities();
        }

        // If forceNew is true or there's no currentPersonaId, create a new persona
        // Otherwise, update the existing persona
        const personaId = forceNew ? undefined : currentPersonaId;
        
        // Use the configured api client instead of axios
        // Make sure to include a leading slash for the path
        const response = await api.post('/persona-engine/engineering/publish', { 
          state, 
          persona_id: personaId,
          name,
          description,
          is_public: isPublic
        });
        
        const newPersonaId = response.data.persona_id;
        
        set({ 
          isLoading: false,
          currentPersonaId: newPersonaId,
          isDirty: false,  // Mark as clean after save
          state: {
            ...get().state!,
            display_name: name,
            description: description ?? get().state?.description ?? '',
          }
        });
        
        return newPersonaId;
      } catch (error) {
        set({ 
          error: error instanceof Error ? error.message : 'Unknown error', 
          isLoading: false 
        });
        return '';
      }
    },
    
    loadPersona: async (personaId: string) => {
      set({ isLoading: true, error: null });
      
      try {
        // Use the existing persona API
        const response = await api.get(`/personas/${personaId}`);
        const persona = response.data;
        
        // Check if it has an engineering profile
        // Note: get_persona_by_id returns the JSONB root blob directly,
        // so engineering_profile lives under persona.data.data (the nested data field)
        let state: WorkingState | null = null;

        // The GET /personas/{id} endpoint returns the JSONB root blob flattened
        // directly into the response — so persona IS the blob, not persona.data.
        // engineering_profile lives at persona.data (the nested data field in the blob)
        // which maps to persona.data on the response object.
        // The GET /personas/{id} route returns the raw JSONB blob dict.
        // engineering_profile lives under persona.data (the nested data field).
        const engineeringProfile =
          persona?.data?.engineering_profile?.current_state   // ← correct path
          ?? persona?.engineering_profile?.current_state;     // future-proof fallback

        if (engineeringProfile) {
          state = engineeringProfile;
          // Older engineering profiles (especially graft-script-imported
          // ones — see backend/app/scripts/persona_authoring/*.py) were
          // persisted without populating display_name / description.
          // persona.name is the canonical name on the Persona record;
          // fall back to it so the header doesn't read "Untitled Persona"
          // for personas that clearly have a name. Same for description.
          // Operator hit this on persona 321f92ae 2026-05-17 (name was
          // "Production_Prompt_Engineer" but display_name was null).
          if (!state.display_name) state.display_name = persona.name;
          if (!state.description) state.description = persona.description ?? '';
        } else {
          // Create a new state from the persona data
          // Use fullDescription or prompt as the character bible if available
          const characterBible = persona.fullDescription || persona.prompt || `You are ${persona.name}. ${persona.shortDescription || ''}`;
          
          state = {
            id: generateUUID(),
            version: 1,
            last_updated: new Date().toISOString(),
            stage: FabricationStage.GENESIS,
            stage_locked: false,
            character_bible: characterBible,
            tethics: persona.tethics || [],
            patterns: persona.patterns || [],
            antipatterns: persona.antipatterns || [],
            conversation_source: [],
            conversation_cockpit: [],
            conversation_verification: [],
            controller_prompt: '',
            ui_schema: [],
            system_prompt: persona.prompt || '',
            probes: [],
            is_live_mode: false,
            active_panel_index: 0,
            tags: persona.tags || [],
            capabilities: persona.capabilities || {},
            
            // New fields
            display_name: persona.name,
            description: persona.description || '',
            revision: 1,
            origin_id: null,
            remix_point: null,
            remix_chain: [],
            created_by: null,
            contributors: []
          };
        }
        
        // Migrate legacy flat-int capabilities to CapabilityEntry shape
        if (state.capabilities) {
          state.capabilities = migrateCapabilities(state.capabilities) as any;
        }

        // Ensure controller_prompt is set
        if (!state.controller_prompt) {
          state.controller_prompt = assembleControllerPrompt(state);
        }
        
        // Compute hashes
        state = await computeStateHashes(state);

        // Land on the highest tab that has meaningful saved content.
        // Priority: Capabilities > Probes > Artifact > Tuning > Source
        const bestPanel = (() => {
          if (Object.keys(state.capabilities || {}).length > 0) return 4;
          if (((state.probes ?? [])?.length ?? 0) > 0) return 3;
          if (state.system_prompt) return 2;
          if ((state.ui_schema?.length ?? 0) > 0) return 1;
          return 0;
        })();
        state = { ...state, active_panel_index: bestPanel };

        set({
          state,
          isLoading: false,
          currentPersonaId: personaId,
          isDirty: false,
          auto_mode: false,
          needs_recompile: false,
          // Loaded persona already has controls from the DB — mark them as
          // "already generated" so the Tuning tab auto-fire guard doesn't
          // regenerate (which would overwrite user-saved controls AND mark
          // the compiled prompt stale).
          hasAutoGeneratedControls: (state.ui_schema?.length ?? 0) > 0,
          stage_completion: computeStageCompletion(state),
        });

        return true;
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Unknown error',
          isLoading: false
        });
        return false;
      }
    },

    remixPersona: async (personaId: string, name?: string, description?: string) => {
      set({ isLoading: true, error: null });
      
      try {
        const response = await api.post(`/persona-engine/engineering/remix/${personaId}`, {
          name,
          description
        });
        
        let state = response.data;
        
        // Ensure controller_prompt is set
        if (!state.controller_prompt) {
          state.controller_prompt = assembleControllerPrompt(state);
        }
        
        // Compute hashes
        state = await computeStateHashes(state);
        
        set({
          state,
          isLoading: false,
          currentPersonaId: null,
          isDirty: true,
          auto_mode: true,
          needs_recompile: true,
          // Remix inherits parent's ui_schema — don't auto-regen controls on
          // first Tuning visit. User can manually regenerate in the Cockpit
          // panel if they want a fresh set tuned to the remix direction.
          hasAutoGeneratedControls: (state.ui_schema?.length ?? 0) > 0,
          stage_completion: computeStageCompletion(state),
        });

        return true;
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Unknown error',
          isLoading: false
        });
        return false;
      }
    },

    generateProbes: async (count, focusAreas) => {
      const { state, currentPersonaId } = get();
      if (!state || !currentPersonaId) {
        set({ probesError: 'Save your persona first before generating probes.' });
        return;
      }
      if (!state.system_prompt) {
        set({ probesError: 'Compile the artifact before generating probes.' });
        return;
      }
      set({ probesLoading: true, probesError: null });
      try {
        const existingProbeIds = (state.probes ?? []).map(p => p.id);
        const newProbes = await service.generateProbes(
          currentPersonaId, count, focusAreas, existingProbeIds,
        );
        const normalizedProbes = newProbes.map(p => ({
          ...p,
          run_history: p.run_history ?? [],
        }));
        const updatedState: WorkingState = {
          ...state,
          probes: [...(state.probes ?? []), ...normalizedProbes],
          probes_generated_from_hash: state.system_prompt_hash ?? undefined,
          probes_last_generated_at: new Date().toISOString(),
          probe_suite_version: (state.probe_suite_version ?? 0) + 1,
        };
        set({
          state: updatedState,
          probesLoading: false,
          isDirty: true,
          stage_completion: computeStageCompletion(updatedState),
        });
      } catch (e) {
        set({
          probesError: e instanceof Error ? e.message : 'Failed to generate probes',
          probesLoading: false,
        });
      }
    },

    markProbeResult: async (probeId, passed, response, notes) => {
      const { state, currentPersonaId } = get();
      if (!state) return;

      const runRecord: ProbeRunRecord = {
        id: generateUUID(),
        run_at: new Date().toISOString(),
        input_prompt: (state.probes ?? []).find(p => p.id === probeId)?.input_prompt ?? '',
        response,
        passed,
        judged_by: 'user',
        system_prompt_hash: state.system_prompt_hash,
        controls_snapshot: Object.fromEntries(
          state.ui_schema.map(c => [c.id, c.current_value])
        ),
        notes,
      };

      const updatedProbes = (state.probes ?? []).map(p => {
        if (p.id !== probeId) return p;
        const newHistory = [runRecord, ...(p.run_history ?? [])].slice(0, 20);
        return {
          ...p,
          run_history: newHistory,
          last_response: response,
          last_passed: passed,
          last_run_at: runRecord.run_at,
        };
      });

      const updatedState = { ...state, probes: updatedProbes };

      set({
        state: updatedState,
        isDirty: true,
        pendingJudgmentProbeId: null,
        pendingJudgmentResponse: null,
        probeRunStates: { ...get().probeRunStates, [probeId]: 'idle' },
        stage_completion: computeStageCompletion(updatedState),
      });

      // Auto-persist — non-fatal
      if (currentPersonaId) {
        try {
          await service.recordProbeRun(currentPersonaId, probeId, runRecord);
        } catch (e) {
          console.warn('Failed to auto-persist probe run result:', e);
        }
      }
    },

    deleteProbe: (probeId) => {
      const { state } = get();
      if (!state) return;
      const updatedState = { ...state, probes: (state.probes ?? []).filter(p => p.id !== probeId) };
      set({ state: updatedState, isDirty: true, stage_completion: computeStageCompletion(updatedState) });
    },

    addCustomProbe: (probe) => {
      const { state } = get();
      if (!state) return;
      const newProbe: DiagnosticProbe = {
        ...probe,
        id: generateUUID(),
        run_history: [],
        created_at: new Date().toISOString(),
        is_custom: true,
      };
      const updatedState = { ...state, probes: [...(state.probes ?? []), newProbe] };
      set({ state: updatedState, isDirty: true, stage_completion: computeStageCompletion(updatedState) });
    },

    updateProbe: (probeId, updates) => {
      const { state } = get();
      if (!state) return;
      const updatedState = {
        ...state,
        probes: (state.probes ?? []).map(p => p.id === probeId ? { ...p, ...updates } : p),
      };
      set({ state: updatedState, isDirty: true });
    },

    extractCapabilities: async (count = 8, depth = 'standard') => {
      const { state } = get();
      if (!state?.system_prompt) {
        set({ capabilitiesError: 'Compile the artifact before extracting capabilities.' });
        return;
      }
      set({ capabilitiesLoading: true, capabilitiesError: null });
      try {
        const entries = await service.extractCapabilities(state, count, depth);
        const capsMap: Record<string, CapabilityEntry> = {};
        for (const entry of entries) {
          capsMap[entry.key] = entry;
        }
        // Re-read state at write time — avoids clobbering changes made during the async call
        const fresh = get().state;
        if (!fresh) return;
        const updatedState = { ...fresh, capabilities: capsMap };
        set({
          state: updatedState,
          isDirty: true,
          capabilitiesLoading: false,
          stage_completion: computeStageCompletion(updatedState),
        });
      } catch (e) {
        set({
          capabilitiesError: e instanceof Error ? e.message : 'Capability extraction failed',
          capabilitiesLoading: false,
        });
      }
    },

    updateCapability: (key, value) => {
      const { state } = get();
      if (!state) return;
      const updatedState = {
        ...state,
        capabilities: { ...(state.capabilities || {}), [key]: value },
      };
      set({ state: updatedState, isDirty: true, stage_completion: computeStageCompletion(updatedState) });
    },

    addCapability: (key, value) => {
      const { state } = get();
      if (!state) return;
      const trimmed = key.trim();
      if (!trimmed) return;
      const updatedState = {
        ...state,
        capabilities: { ...(state.capabilities || {}), [trimmed]: value },
      };
      set({ state: updatedState, isDirty: true, stage_completion: computeStageCompletion(updatedState) });
    },

    removeCapability: (key) => {
      const { state } = get();
      if (!state) return;
      const caps = { ...(state.capabilities || {}) };
      delete caps[key];
      const updatedState = { ...state, capabilities: caps };
      set({ state: updatedState, isDirty: true, stage_completion: computeStageCompletion(updatedState) });
    },

    setActiveProbeId: (id) => set({ activeProbeId: id }),

    clearPendingJudgment: () => set({
      pendingJudgmentProbeId: null,
      pendingJudgmentResponse: null,
    }),

    receiveSendTo: (payload) => {
      const { content, action } = payload;
      if (action === 'genesis') {
        get().init(content);
      } else if (action === 'mutate') {
        get().sendSourceMessage(content);
      }
      // 'evidence' goes to suggestion queue via API — handled by backend
    },

    markClean: () => {
      set({ isDirty: false });
    },
    
    resetState: () => {
      set({
        state: null,
        isLoading: false,
        error: null,
        currentPersonaId: null,
        isDirty: false,
        current_source_hash: null,
        artifact_source_hash: null,
        auto_mode: true,
        needs_recompile: false,
        stage_completion: { genesis: false, tuning: false, artifact: false, probes: false, capabilities: false },
        pendingTestDriveMessage: null,
        pendingMapFocus: null,
        capabilitiesLoading: false,
        capabilitiesError: null,
        hasAutoGeneratedControls: false,
        probesLoading: false,
        probesError: null,
        probeRunStates: {},
        activeProbeId: null,
        pendingJudgmentProbeId: null,
        pendingJudgmentResponse: null,
        autoSaveStatus: 'idle',
        lastAutoSavedAt: null,
      });
    }
  };
});
